import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { RealtimeProvider } from "@/contexts/RealtimeContext";
import Layout from "@/components/Layout";
import CelebrationOverlay from "@/components/CelebrationOverlay";
import SprintOverlay from "@/components/SprintOverlay";
import ErrorBoundary from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import FunilBoard from "@/pages/FunilBoard";
import DashboardGeral from "@/pages/DashboardGeral";
import DashboardNucleo from "@/pages/DashboardNucleo";
import TVMode from "@/pages/TVMode";
import GestaoPDI from "@/pages/GestaoPDI";
import Agenda from "@/pages/Agenda";
import InteligenciaComercial from "@/pages/InteligenciaComercial";
import Proposta from "@/pages/Proposta";
import MunicipioPerfil from "@/pages/MunicipioPerfil";
import PessoaPerfil from "@/pages/PessoaPerfil";
import Admin from "@/pages/Admin";
import Carteira from "@/pages/Carteira";
import Fiscal from "@/pages/Fiscal";
import MinhasNfs from "@/pages/MinhasNfs";
import NovoLead from "@/pages/NovoLead";
import LeadsPendentes from "@/pages/LeadsPendentes";
import Notificacoes from "@/pages/Notificacoes";
import CaptacaoBase from "@/pages/CaptacaoBase";
import GestaoCursos from "@/pages/GestaoCursos";
import ValidarDocumento from "@/pages/ValidarDocumento";
import MinhasIndicacoes from "@/pages/MinhasIndicacoes";import CelKanban from "@/pages/CelKanban";
import CelDashboard from "@/pages/CelDashboard";
import CelAdmin from "@/pages/CelAdmin";
import CelCarteira from "@/pages/CelCarteira";
import CelLeadsAcao from "@/pages/CelLeadsAcao";
import RelatorioFaturamento from "@/pages/RelatorioFaturamento";
import "@/index.css";

// Home route por papel (Iter 50: adicionado indicador/gestao_cel e vendedora CeL)
export function defaultHome(user) {
  if (!user) return "/login";
  if (user.role === "fiscal") return "/fiscal";
  if (user.role === "analista_mkt") return "/novo-lead";
  if (user.role === "indicador") return "/minhas-indicacoes";
  if (user.role === "gestao_cel") return "/cel/kanban";
  if (user.role === "vendedora" && user.nucleo_nome === "CeL Consultoria") return "/cel/kanban";
  return "/funil";
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Carregando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={defaultHome(user)} replace />;
  }
  return children;
}

function AppShell() {
  return (
    <RealtimeProvider>
      <CelebrationOverlay />
      <SprintOverlay />
      <Layout>
        <ErrorBoundary>
          <Routes>
            <Route path="/funil" element={<Protected><FunilBoard /></Protected>} />
            <Route path="/funil/:cursoId" element={<Protected><FunilBoard /></Protected>} />
            <Route path="/carteira" element={<Protected><Carteira /></Protected>} />
            <Route path="/fiscal" element={<Protected><Fiscal /></Protected>} />
            <Route path="/fiscal/faturamento" element={<Protected roles={["fiscal", "diretoria"]}><RelatorioFaturamento /></Protected>} />
            <Route path="/minhas-nfs" element={<Protected><MinhasNfs /></Protected>} />
            <Route path="/geral" element={<Protected><DashboardGeral /></Protected>} />
            <Route path="/nucleo" element={<Navigate to="/geral" replace />} />
            <Route path="/guerra" element={<Navigate to="/geral" replace />} />
            <Route path="/resultado" element={<Navigate to="/geral" replace />} />
            <Route path="/inteligencia" element={<Protected><InteligenciaComercial /></Protected>} />
            <Route path="/proposta/:cardId" element={<Protected><Proposta /></Protected>} />
            <Route path="/agenda" element={<Protected><Agenda /></Protected>} />
            <Route path="/historico" element={<Navigate to="/geral" replace />} />
            <Route path="/pdi" element={<Protected roles={["diretoria"]}><GestaoPDI /></Protected>} />
            <Route path="/municipio/:id" element={<Protected><MunicipioPerfil /></Protected>} />
            <Route path="/pessoa/:id" element={<Protected><PessoaPerfil /></Protected>} />
            <Route path="/admin" element={<Protected roles={["diretoria", "supervisao"]}><Admin /></Protected>} />
            <Route path="/novo-lead" element={<Protected roles={["analista_mkt", "diretoria"]}><NovoLead /></Protected>} />
            <Route path="/leads-pendentes" element={<Protected roles={["diretoria", "supervisao"]}><LeadsPendentes /></Protected>} />
            <Route path="/notificacoes" element={<Protected><Notificacoes /></Protected>} />
            <Route path="/captacao" element={<Protected roles={["analista_mkt", "diretoria"]}><CaptacaoBase /></Protected>} />
            <Route path="/gestao-cursos" element={<Protected roles={["diretoria", "supervisao"]}><GestaoCursos /></Protected>} />
            <Route path="/minhas-indicacoes" element={<Protected><MinhasIndicacoes /></Protected>} />
            <Route path="/cel/kanban" element={<Protected roles={["diretoria", "gestao_cel", "vendedora", "indicador"]}><CelKanban /></Protected>} />
            <Route path="/cel/dashboard" element={<Protected roles={["diretoria", "gestao_cel", "vendedora"]}><CelDashboard /></Protected>} />
            <Route path="/cel/admin" element={<Protected roles={["diretoria", "gestao_cel"]}><CelAdmin /></Protected>} />
            <Route path="/cel/carteira" element={<Protected roles={["diretoria", "gestao_cel", "vendedora"]}><CelCarteira /></Protected>} />
            <Route path="/cel/leads-acao" element={<Protected roles={["diretoria", "gestao_cel"]}><CelLeadsAcao /></Protected>} />
            <Route path="/" element={<Navigate to="/funil" replace />} />
          </Routes>
        </ErrorBoundary>
      </Layout>
    </RealtimeProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors closeButton={false} duration={3000} />
      <Routes>
        <Route path="/tv" element={<ErrorBoundary><TVMode /></ErrorBoundary>} />
        <Route path="/validar/:codigo" element={<ErrorBoundary><ValidarDocumento /></ErrorBoundary>} />
        <Route path="/login" element={<AuthProvider><ErrorBoundary><Login /></ErrorBoundary></AuthProvider>} />
        <Route path="/*" element={<AuthProvider><AppShell /></AuthProvider>} />
      </Routes>
    </BrowserRouter>
  );
}
